import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  getCustomerDetail,
  getProductDetail,
  listDevices,
  listUsers,
} from "./api";
import type { CustomerDetail } from "./types";

/**
 * Enrichment hooks — installation rows carry IDs only; these hydrate display
 * fields (customer name/ward, product name, device serial, technician name)
 * from the owning services. All are best-effort: failures fall back to IDs.
 */

export function useCustomersMap(ids: number[]): Map<number, CustomerDetail> {
  const unique = useMemo(() => [...new Set(ids)].filter((id) => id > 0), [ids]);
  const results = useQueries({
    queries: unique.map((id) => ({
      queryKey: ["inst-customer", id],
      queryFn: () => getCustomerDetail(id),
      retry: 1,
      staleTime: 60_000,
    })),
  });
  return useMemo(() => {
    const map = new Map<number, CustomerDetail>();
    results.forEach((r) => {
      if (r.data) map.set(r.data.customer.id, r.data.customer);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results.map((r) => r.data).join(",")]);
}

export function useProductsMap(ids: (number | null)[]): Map<number, string> {
  const unique = useMemo(
    () => [...new Set(ids.filter((id): id is number => typeof id === "number" && id > 0))],
    [ids],
  );
  const results = useQueries({
    queries: unique.map((id) => ({
      queryKey: ["inst-product", id],
      queryFn: () => getProductDetail(id),
      retry: 1,
      staleTime: 300_000,
    })),
  });
  return useMemo(() => {
    const map = new Map<number, string>();
    results.forEach((r) => {
      if (r.data) map.set(r.data.product.id, r.data.product.name);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results.map((r) => r.data).join(",")]);
}

/** Device id → serial number (office queue). */
export function useDeviceSerialMap(enabled = true): Map<number, string> {
  const query = useQuery({
    queryKey: ["inst-devices"],
    queryFn: () => listDevices(),
    retry: 1,
    staleTime: 60_000,
    enabled,
  });
  return useMemo(() => {
    const map = new Map<number, string>();
    query.data?.devices.forEach((d) => map.set(d.id, d.serial_number));
    return map;
  }, [query.data]);
}

/** User id → display name (office queue technician column). USER_MANAGE may
    be denied for some roles — callers fall back to "#<id>". */
export function useUserNameMap(enabled = true): Map<number, string> {
  const query = useQuery({
    queryKey: ["inst-users"],
    queryFn: () => listUsers(),
    retry: 1,
    staleTime: 300_000,
    enabled,
  });
  return useMemo(() => {
    const map = new Map<number, string>();
    query.data?.users.forEach((u) => map.set(u.id, `${u.first_name} ${u.last_name}`.trim() || u.username));
    return map;
  }, [query.data]);
}

export function customerDisplayName(c: CustomerDetail | undefined, fallbackId: number): string {
  if (!c) return `Customer #${fallbackId}`;
  return `${c.first_name} ${c.last_name}`.trim() || c.customer_number;
}
